import cn from 'classnames'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { P360NotInScopeWidgetPlaceholder } from 'legacy/lut/components/widgets/public/partner360/components/p360NotInScopeWidgetPlaceholder/P360NotInScopeWidgetPlaceholder'
import { HelpButton, HelpSection } from 'legacy/lut/components/widgets/public/partner360/helpButton'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { P360WidgetPlaceholder } from 'legacy/lut/components/widgets/public/partner360/P360WidgetPlaceholder'
import {
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { IPartnerContact } from 'partner360/features/contacts'
import { CockpitCardHeader } from 'partner360/features/financialCockpit/ui/components/cockpitCardHeader/CockpitCardHeader'
import { withPartner360Checks } from 'partner360/hoc/withPartner360Checks'

import { usePartnerContacts } from '../../hooks/usePartnerContacts'
import { TRANSLATION_KEYS } from '../../model/constants'
import { ExtendedPartnerContactsDialog } from '../extendedPartnerContactsDialog/ExtendedPartnerContactsDialog'
import { PartnerContactCard } from '../partnerContactCard/PartnerContactCard'

import { IContactsTableProps } from './models/IContactsTableProps'

import styles from './ContactsTable.module.scss'

export const ContactsTable = withPartner360Checks<IContactsTableProps>(
  ({ pid, widgetParams, metrics, isEmbeded }) => {
    /** -------------------- Variables ----------------------- */
    const [showExtendedModal, setShowExtendedModal] = useState(false)

    const { isGlobal } = useSelector(selectPartnerAccess)
    const isEditMode = useSelector(selectPublishingMode)
    const programType = useSelector(selectProgramType)
    const partnerId = usePartnerId(widgetParams, pid)

    const { isPartnerUser } = useUserRoles()
    /* The cockpit's `Overall` is no offering of its own: it lists the contacts of every offering,
       read only, as the list's editor saves a contact to one offering. */
    const isEveryOffering = programType === ProgramTypes.Overall
    const { data: contacts, isLoading } = usePartnerContacts({
      programType: isEveryOffering ? null : programType,
      partnerId,
    })

    const isEditAllowed = isEditMode && !isPartnerUser && !isEveryOffering

    const contactCards = useMemo(() => {
      return contacts.map((contact: IPartnerContact) => {
        return <PartnerContactCard key={contact.UID} contact={contact} />
      })
    }, [contacts])

    return (
      <P360Card
        fullHeight
        className={styles.surface}
        header={
          <CockpitCardHeader
            title={<Translation text={TRANSLATION_KEYS.widgetTitle} />}
            actions={
              <>
                {isEditAllowed && (
                  <Button
                    size="sm"
                    variant="outlined"
                    icon="kbase kbase-edit"
                    iconPosition="end"
                    onClick={() => setShowExtendedModal(true)}
                  >
                    <Translation text={TRANSLATION_KEYS.editList} span />
                  </Button>
                )}
                {!isGlobal && <HelpButton section={HelpSection.ContactInfo} />}
              </>
            }
          />
        }
        disabled={isEmbeded}
      >
        <div className={cn(styles.root)} id="ContactsTable" data-testid={'contactsTable-root'}>
          <div className={styles.container}>
            <LoaderOverlay isVisible={isLoading} />
            <div className={styles.cardsScroller}>
              <div className={styles.cardsList}>{contactCards}</div>
              {contactCards.length === 0 && (
                <div className={styles.emptyState}>
                  <Translation text={TRANSLATION_KEYS.emptyList} span />
                </div>
              )}
            </div>
            <ExtendedPartnerContactsDialog
              show={showExtendedModal}
              partnerId={partnerId}
              programType={programType}
              metrics={metrics}
              onClose={() => setShowExtendedModal(false)}
            />
          </div>
        </div>
      </P360Card>
    )
  },
  {
    accessOverlayPlaceholder: <P360WidgetPlaceholder />,
    scopeOverlayPlaceholder: <P360NotInScopeWidgetPlaceholder />,
  }
)

ContactsTable.displayName = 'Protected(ContactsTable)'

export default ContactsTable
